import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2 } from "lucide-react";
import { api } from "../../lib/api";
import { isStockDepot } from "../../lib/stockDepot";
import RecapUnmatched from "./RecapUnmatched";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../ui/dialog";

const currentYear = new Date().getFullYear();
const MONTHS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

function Field({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Label className="mb-1.5 block">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid}><SelectValue placeholder="Choisir…" /></SelectTrigger>
        <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

export default function NayaxRecapImportDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [dists, setDists] = useState([]);
  const [kind, setKind] = useState("monthly");
  const [distId, setDistId] = useState("");
  const [year, setYear] = useState(String(currentYear));
  const [mFrom, setMFrom] = useState("1");
  const [mTo, setMTo] = useState("12");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [unmatched, setUnmatched] = useState([]);

  useEffect(() => {
    if (!open || dists.length) return;
    api.get("/distributors").then((r) =>
      setDists((r.data || []).filter((d) => d.type === "AUTOMATIQUE" && !isStockDepot(d))));
  }, [open, dists.length]);

  const submit = async () => {
    const fd = new FormData();
    fd.append("file", file);
    const cfg = { headers: { "Content-Type": "multipart/form-data" } };
    setBusy(true);
    try {
      if (kind === "monthly") {
        const { data } = await api.post(`/statistics/nayax-monthly-recap/import?distributor_id=${distId}&year=${year}`, fd, cfg);
        toast.success(data.twint_only
          ? `${data.distributor_name} ${data.year} : transactions TWINT de ${data.months.length} mois importées`
          : `${data.distributor_name} ${data.year} : ${data.months.length} mois importés (CHF ${data.total_ca.toFixed(2)})`);
        if (data.skipped_current_month) toast.info("Le mois en cours n'est pas importé : il reste suivi en direct par AgroManage.");
        setOpen(false);
      } else {
        const m = `&month=${mFrom}&month_to=${mTo}`;
        const { data } = await api.post(`/statistics/nayax-product-recap/import?distributor_id=${distId}&year=${year}${m}`, fd, cfg);
        toast.success(`${data.distributor_name} ${data.period} : ${data.lines.length} plats importés (CHF ${data.total.toFixed(2)})`);
        setUnmatched(data.unmatched || []);
        if (!data.unmatched?.length) setOpen(false);
      }
      onDone?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Import impossible");
    } finally { setBusy(false); }
  };

  const reset = (v) => { setOpen(v); if (v) { setUnmatched([]); setFile(null); } };
  const monthOpts = MONTHS.map((l, i) => [String(i + 1), l]);
  const years = [currentYear, currentYear - 1, currentYear - 2].map((y) => [String(y), String(y)]);

  return (
    <Dialog open={open} onOpenChange={reset}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="nayax-recap-import-btn"><Upload className="w-4 h-4 mr-2" /> Importer un récap Nayax</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="nayax-recap-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Importer un récap Nayax</DialogTitle>
          <DialogDescription>
            Fichier Excel Nayax pour une seule machine. Les chiffres Nayax remplacent ceux d&apos;AgroManage dans les
            statistiques uniquement : stocks, lots et rétributions ne changent pas. Réimporter met à jour.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="Type de fichier" value={kind} onChange={setKind} testid="nayax-recap-kind" options={[
            ["monthly", "Par mois — « Analyse des ventes » (tous paiements, ou filtré Twint)"],
            ["product", "Par plat — « Résumé des ventes » (Best-Sellers)"],
          ]} />
          <Field label="Distributeur" value={distId} onChange={setDistId} testid="nayax-recap-distributor"
            options={dists.map((d) => [d.id, d.name])} />
          <div className={`grid gap-3 ${kind === "product" ? "grid-cols-3" : "grid-cols-1"}`}>
            <Field label="Année" value={year} onChange={setYear} testid="nayax-recap-year" options={years} />
            {kind === "product" && (
              <>
                <Field label="Du mois" value={mFrom} onChange={setMFrom} testid="nayax-recap-month-from" options={monthOpts} />
                <Field label="Au mois (inclus)" value={mTo} onChange={setMTo} testid="nayax-recap-month-to" options={monthOpts} />
              </>
            )}
          </div>
          {kind === "product" && (
            <p className="text-[11px] text-[#575A57]">Indiquez exactement la période choisie dans Nayax (ex. janvier → août).</p>
          )}
          <Input type="file" accept=".xlsx" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="nayax-recap-file" />
          {unmatched.length > 0 && <RecapUnmatched names={unmatched} onReimport={submit} busy={busy} />}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Fermer</Button>
          <Button onClick={submit} disabled={!distId || !file || busy} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="nayax-recap-submit">
            {busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Importer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
